import { Button, Dropdown, Input, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import { useHandlers } from '../../../context/page-context';
import { useSettings } from '../../../context/settings-context';
import { stringToSlug } from '../../../utils/slug';
import { useShippingMethodForm } from '../builder-context';
import { type ShippingBandBuilderState, shippingBandCostSchema } from '../schemas';
import type { ShippingBandApiResponse } from '../types/shipping-method';

function prefixLanguageString(languageString: string) {
  return `shipping.custom-message.${languageString}`;
}

function getDefaultCostState(
  costType: ShippingBandApiResponse['cost']['type'],
  bandName: string,
): ShippingBandBuilderState['cost'] {
  switch (costType) {
    case 'free':
      return { type: 'free' };
    case 'fixed_price':
      return { type: 'fixed_price', price: '' };
    case 'percent_net_total':
      return {
        type: 'percent_net_total',
        percentageCharge: '',
      };
    case 'custom':
      return {
        type: 'custom',
        costLanguageString: stringToSlug(bandName),
      };
  }
}

// Each cost type has different properties associated with it, so will show different form fields
export function BandCost({ band, index }: { band: ShippingBandBuilderState; index: number }) {
  const form = useShippingMethodForm();
  const { notificationHandler } = useHandlers();

  const settings = useSettings();
  const baseCurrency = settings.baseCurrency;

  return (
    <>
      {/* Shipping band cost selector */}
      <form.Field name={`bands[${index}].cost`}>
        {(field) => (
          <Dropdown
            label={t('features:shipping_methods.components.band_cost.label')}
            options={shippingBandCostSchema.options.map((option) => ({
              label: t(
                `features:shipping_methods.components.band_cost.options.${option.shape.type.value}`,
              ),
              value: option.shape.type.value,
            }))}
            // Falls back to '' because value becomes `undefined` momentarily when deleting band
            value={field.state.value?.type ?? ''}
            // Set default values to show in form fields for selected cost type
            onChange={(value) =>
              field.handleChange(
                getDefaultCostState(value as ShippingBandApiResponse['cost']['type'], band.name),
              )
            }
          />
        )}
      </form.Field>

      <form.Subscribe selector={(state) => state.values.bands[index]?.cost?.type}>
        {(costType) =>
          costType === 'fixed_price' && (
            // Fixed price input
            <form.Field name={`bands[${index}].cost.price`}>
              {(field) => (
                <Input
                  inputMode="numeric"
                  label={t('features:shipping_methods.components.band_cost.fixed_price.label')}
                  placeholder={t(
                    'features:shipping_methods.components.band_cost.fixed_price.placeholder',
                  )}
                  suffix={baseCurrency}
                  value={field.state.value}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]?.message}
                />
              )}
            </form.Field>
          )
        }
      </form.Subscribe>

      <form.Subscribe selector={(state) => state.values.bands[index]?.cost?.type}>
        {(costType) =>
          costType === 'percent_net_total' && (
            // Percentage input
            <form.Field name={`bands[${index}].cost.percentageCharge`}>
              {(field) => (
                <Input
                  inputMode="numeric"
                  label={t(
                    'features:shipping_methods.components.band_cost.percent_net_total.label',
                  )}
                  placeholder={t(
                    'features:shipping_methods.components.band_cost.percent_net_total.placeholder',
                  )}
                  suffix="%"
                  value={field.state.value}
                  onChange={field.handleChange}
                  error={field.state.meta.errors[0]?.message}
                />
              )}
            </form.Field>
          )
        }
      </form.Subscribe>

      {/* Custom shows user a language string that is derived from the band name. They can configure what it says in sparkOptions on the frontend */}
      <form.Subscribe selector={(state) => state.values.bands[index]?.cost}>
        {(cost) =>
          cost?.type === 'custom' && (
            <>
              <div className="flex w-full items-end gap-2">
                <span className="flex-1">
                  <Input
                    value={prefixLanguageString(cost.costLanguageString)}
                    label={t('features:shipping_methods.components.band_cost.custom.label')}
                    disabled
                    readOnly
                  />
                </span>
                <Button
                  icon="copy"
                  variant="secondary"
                  onClick={() => {
                    if (band.cost.type === 'custom') {
                      navigator.clipboard
                        .writeText(prefixLanguageString(band.cost.costLanguageString))
                        .then(() => notificationHandler.success(t('notifications:copied')));
                    }
                  }}
                >
                  {t('buttons:copy')}
                </Button>
              </div>
              <Text variant="secondary" size="sm">
                {t('features:shipping_methods.components.band_cost.custom.info')}{' '}
                <LearnMoreLink link="https://docs.sparklayer.io/languages-and-international#pt8wF" />
              </Text>
            </>
          )
        }
      </form.Subscribe>
    </>
  );
}
